import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Table, Checkbox, Group, Avatar, Text, Box, Badge,
  ActionIcon, Tooltip, Pagination, Stack,
} from '@mantine/core'
import { IconUserCheck, IconUserOff, IconRefresh } from '@tabler/icons-react'
import { ROLE_COLORS } from '@/api/roles'
import { getKanidmLabel } from './kanidmStatus'
import { isPendingRow, rowKey, selectableIds } from './rowKey'

export interface UserRow {
  /**
   * `users.id`. ATENÇÃO: cadastros pendentes chegam do backend com `id: null`
   * (o id deles é `registration_id`). Nunca use `id` como chave ou identidade
   * de linha — use os helpers de `./rowKey`.
   */
  id: number
  type?: 'user' | 'pending_registration'
  email: string
  full_name: string
  is_active: boolean
  roles: string[]
  tenants: { id: number; name: string }[]
  ldap_synced: boolean | null
  must_change_password?: boolean
  created_at?: string
  last_login_at?: string | null
  status?: string
  registration_id?: number
  phone?: string
  address?: string
  organization_name?: string
  employee_number?: string
}

interface UsersTableProps {
  users: UserRow[]
  duplicateEmails: Set<string>
  selectedIds: Set<number>
  onSelectedIdsChange: (ids: Set<number>) => void
  selectedUserId: number | null
  /** Identidade da linha aberta no drawer (`rowKey`). Precisa para pendentes,
   *  que não têm `id`. Quando ausente, cai em `selectedUserId`. */
  selectedRowKey?: string | null
  onSelectUser: (user: UserRow) => void
  onToggleActive: (userId: number, isActive: boolean) => void
  onSyncLdap: (userId: number) => void
  toggleActiveLoading?: boolean
  syncLdapLoading?: boolean
  /** Muda quando busca/filtros mudam → volta para a página 1. NÃO deve mudar em
   *  refetch: antes o reset dependia de `users` e qualquer invalidação de query
   *  (ex.: desativar alguém) jogava o admin de volta para a primeira página. */
  resetPageKey?: string
}

const PAGE_SIZE = 10

function getInitials(name: string, email: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return email?.slice(0, 2).toUpperCase() ?? 'U'
}

export function UsersTable({
  users,
  duplicateEmails,
  selectedIds,
  onSelectedIdsChange,
  selectedUserId,
  selectedRowKey,
  onSelectUser,
  onToggleActive,
  onSyncLdap,
  toggleActiveLoading,
  syncLdapLoading,
  resetPageKey,
}: UsersTableProps) {
  const [page, setPage] = useState(1)

  // Volta para a página 1 só quando a consulta muda (busca/filtro), não em
  // refetch. `safePage` abaixo já protege contra a lista encurtar.
  useEffect(() => {
    setPage(1)
  }, [resetPageKey])

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)

  const paginatedUsers = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE
    return users.slice(start, start + PAGE_SIZE)
  }, [users, safePage])

  // Só usuários reais entram na seleção — pendentes não têm id.
  const pageIds = useMemo(
    () => new Set(selectableIds(paginatedUsers)), [paginatedUsers],
  )

  const allPageSelected = pageIds.size > 0 && [...pageIds].every(id => selectedIds.has(id))
  const somePageSelected = [...pageIds].some(id => selectedIds.has(id))

  const handleHeaderCheckbox = useCallback(() => {
    const next = new Set(selectedIds)
    if (allPageSelected) {
      for (const id of pageIds) next.delete(id)
    } else {
      for (const id of pageIds) next.add(id)
    }
    onSelectedIdsChange(next)
  }, [selectedIds, allPageSelected, pageIds, onSelectedIdsChange])

  const handleRowCheckbox = useCallback((user: UserRow) => {
    if (isPendingRow(user)) return
    const next = new Set(selectedIds)
    if (next.has(user.id)) next.delete(user.id)
    else next.add(user.id)
    onSelectedIdsChange(next)
  }, [selectedIds, onSelectedIdsChange])

  const handleRowClick = useCallback((e: React.MouseEvent, user: UserRow) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-no-row-click]')) return
    onSelectUser(user)
  }, [onSelectUser])

  const startItem = (safePage - 1) * PAGE_SIZE + 1
  const endItem = Math.min(safePage * PAGE_SIZE, users.length)

  return (
    <Stack gap="sm">
      <Table.ScrollContainer minWidth={700}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}>
                <Checkbox
                  size="xs"
                  checked={allPageSelected}
                  indeterminate={somePageSelected && !allPageSelected}
                  onChange={handleHeaderCheckbox}
                  aria-label="Selecionar todos da página"
                  data-no-row-click
                />
              </Table.Th>
              <Table.Th>Usuário</Table.Th>
              <Table.Th style={{ width: 200 }}>Funções</Table.Th>
              <Table.Th style={{ width: 150 }}>Status</Table.Th>
              <Table.Th style={{ width: 80 }}>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedUsers.map((u) => {
              const pending = isPendingRow(u)
              const key = rowKey(u)
              const highlighted = selectedRowKey != null
                ? selectedRowKey === key
                : !pending && selectedUserId === u.id
              return (
              <Table.Tr
                key={key}
                style={{
                  opacity: pending || u.is_active ? 1 : 0.6,
                  backgroundColor: highlighted ? 'var(--ai-border-light, #e6f3f5)' : undefined,
                  cursor: 'pointer',
                }}
                onClick={(e) => handleRowClick(e, u)}
              >
                {/* Checkbox — pendente não é selecionável (ver ./rowKey) */}
                <Table.Td>
                  <Checkbox
                    size="xs"
                    checked={!pending && selectedIds.has(u.id)}
                    disabled={pending}
                    onChange={() => handleRowCheckbox(u)}
                    data-no-row-click
                    aria-label={`Selecionar ${u.full_name || u.email}`}
                  />
                </Table.Td>

                {/* Usuario: avatar + name + email + duplicate badge */}
                <Table.Td>
                  <Group gap="sm">
                    <Avatar
                      size={34}
                      radius="md"
                      color={u.is_active ? 'inovareTeal' : 'gray'}
                      variant="filled"
                    >
                      <Text style={{ fontSize: 12, fontWeight: 700 }}>
                        {getInitials(u.full_name, u.email)}
                      </Text>
                    </Avatar>
                    <Box>
                      <Group gap={6}>
                        <Text size="sm" fw={500} c="var(--ai-text-primary)">
                          {u.full_name || '\u2014'}
                        </Text>
                        {duplicateEmails.has(u.email.toLowerCase()) && (
                          <Badge size="xs" color="red" variant="light">Duplicata?</Badge>
                        )}
                      </Group>
                      <Text size="xs" c="var(--ai-text-muted)">{u.email}</Text>
                    </Box>
                  </Group>
                </Table.Td>

                {/* Roles: max 3 badges + overflow */}
                <Table.Td>
                  <Group gap={4}>
                    {(u.roles ?? []).length === 0 ? (
                      <Text size="xs" c="dimmed">{'\u2014'}</Text>
                    ) : (
                      <>
                        {(u.roles ?? []).slice(0, 3).map((r) => (
                          <Badge key={r} size="xs" variant="light" color={ROLE_COLORS[r] ?? 'blue'}>
                            {r}
                          </Badge>
                        ))}
                        {(u.roles ?? []).length > 3 && (
                          <Badge size="xs" variant="light" color="gray">
                            +{u.roles.length - 3}
                          </Badge>
                        )}
                      </>
                    )}
                  </Group>
                </Table.Td>

                {/* Status: pendente tem estado próprio; usuário real = ativo/inativo + Kanidm */}
                <Table.Td>
                  <Box>
                    {pending ? (
                      <>
                        <Badge size="sm" color="yellow" variant="dot">Pendente</Badge>
                        <Text size="xs" c="var(--ai-text-muted)" mt={2}>
                          Aguardando aprovação
                        </Text>
                      </>
                    ) : (
                      <>
                        <Badge size="sm" color={u.is_active ? 'teal' : 'red'} variant="dot">
                          {u.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Text size="xs" c="var(--ai-text-muted)" mt={2}>
                          {getKanidmLabel(u)}
                        </Text>
                      </>
                    )}
                  </Box>
                </Table.Td>

                {/* Ações — nenhuma para pendente: ativar/desativar e sync não se
                    aplicam a um cadastro (chamavam PUT /v1/users/null → 422).
                    Aprovar/rejeitar fica no drawer, ao clicar na linha. */}
                <Table.Td>
                  {pending ? (
                    <Text size="xs" c="var(--ai-text-muted)">{'—'}</Text>
                  ) : (
                  <Group gap={4} data-no-row-click>
                    <Tooltip label={u.is_active ? 'Desativar' : 'Ativar'}>
                      <ActionIcon
                        variant="subtle"
                        color={u.is_active ? 'red' : 'teal'}
                        size="sm"
                        onClick={() => onToggleActive(u.id, u.is_active)}
                        loading={toggleActiveLoading}
                      >
                        {u.is_active ? <IconUserOff size={14} /> : <IconUserCheck size={14} />}
                      </ActionIcon>
                    </Tooltip>
                    {u.ldap_synced === false && !u.must_change_password && (
                      <Tooltip label="Sincronizar com Kanidm">
                        <ActionIcon
                          variant="subtle"
                          color="orange"
                          size="sm"
                          onClick={() => onSyncLdap(u.id)}
                          loading={syncLdapLoading}
                        >
                          <IconRefresh size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                  )}
                </Table.Td>
              </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* Pagination footer */}
      {users.length > PAGE_SIZE && (
        <Group justify="space-between" px="md" pb="sm">
          <Text size="xs" c="var(--ai-text-muted)">
            Mostrando {startItem}-{endItem} de {users.length}
          </Text>
          <Pagination
            size="sm"
            total={totalPages}
            value={safePage}
            onChange={setPage}
          />
        </Group>
      )}
    </Stack>
  )
}
